import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Toolbar,
  Typography,
  Stack,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { colors } from '../theme';
import headerLogo from '../assets/header.png';
import zonaGalateaLogo from '../assets/zona-galatea.png';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  /** Elemento a sinistra del titolo (es. freccia indietro) */
  leading?: React.ReactNode;
  actions?: React.ReactNode;
  /** Contenuto flottante (FAB) renderizzato sopra tutto il layout */
  floating?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Nasconde il pulsante admin in header */
  hideAdmin?: boolean;
}

const contactLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 1.75,
  py: 1,
  borderRadius: 999,
  bgcolor: colors.surfaceContainer,
  border: `1px solid ${colors.outlineVariant}`,
  color: colors.onSurfaceVariant,
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.2,
  transition:
    'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    bgcolor: colors.primaryContainer,
    color: colors.onPrimaryContainer,
    borderColor: colors.primaryContainer,
  },
} as const;

export function AppLayout({
  children,
  title,
  leading,
  actions,
  floating,
  maxWidth = 'md',
  hideAdmin = false,
}: AppLayoutProps) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, ${colors.primaryContainer}55 0%, ${colors.surface} 28%, ${colors.surface} 100%)`,
      }}
    >
      <AppBar position="sticky" elevation={0}>
        <Toolbar
          sx={{
            gap: { xs: 1, sm: 1.5 },
            minHeight: { xs: 68, sm: 76 },
            px: { xs: 1, sm: 2.5 },
          }}
        >
          {leading}

          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              textDecoration: 'none',
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src={headerLogo}
              alt="Zona Galatea"
              sx={{
                height: { xs: 44, sm: 52 },
                width: 'auto',
                display: 'block',
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                lineHeight: 1.2,
                opacity: 0.85,
                letterSpacing: '0.08em',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}
            >
              AGESCI · Zona Galatea
            </Typography>
            <Typography
              variant="h6"
              component="div"
              noWrap
              sx={{
                color: 'inherit',
                fontWeight: 700,
                lineHeight: 1.25,
                fontSize: { xs: '1.1rem', sm: '1.35rem' },
              }}
            >
              {title || 'Sede di Zona'}
            </Typography>
          </Box>

          {actions}

          {!isAdmin && !hideAdmin && (
            <IconButton
              component={RouterLink}
              to="/admin"
              color="inherit"
              aria-label="Area admin"
              size="large"
            >
              <AdminPanelSettingsOutlinedIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Container
        maxWidth={maxWidth}
        sx={{
          flex: 1,
          py: { xs: 2, sm: 3.5 },
          px: { xs: 1.5, sm: 3 },
          width: '100%',
        }}
      >
        {children}
      </Container>

      <Container
        maxWidth={maxWidth}
        component="footer"
        sx={{
          mt: 'auto',
          width: '100%',
          pt: { xs: 1, sm: 1.5 },
          pb: { xs: 1, sm: 1.25 },
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: '28px',
            bgcolor: colors.surfaceContainer,
            border: `1px solid ${colors.outlineVariant}`,
          }}
        >
          {/* Mobile: logo+info | map full; Desktop: logo full-height | info+map */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 2 },
              alignItems: 'stretch',
            }}
          >
            {/* Top row on mobile / left column on desktop: logo + info (1/2) */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: { xs: 1.25, sm: 2 },
                alignItems: 'stretch',
                flex: { sm: '1 1 0' },
                minWidth: 0,
                padding: { xs: 1, sm: 4 },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'stretch',
                  width: { xs: 64, sm: 100 },
                }}
              >
                <Box
                  component="img"
                  src={zonaGalateaLogo}
                  alt="AGESCI Zona Galatea Sicilia"
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxHeight: { xs: 160, sm: 'none' },
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              </Box>

              <Stack
                spacing={1}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  justifyContent: 'center',
                  textAlign: 'left',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: colors.primary,
                      fontSize: '0.95rem',
                      lineHeight: 1.25,
                    }}
                  >
                    Parco Belfrontizio
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: 'center',
                      color: colors.onSurfaceVariant,
                    }}
                  >
                    <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Via Rocco Chinnici · Acireale
                    </Typography>
                  </Stack>
                </Box>

                <Stack spacing={0.75} sx={{ width: '100%' }}>
                  <Box
                    component="a"
                    href="tel:+393774612000"
                    sx={{
                      ...contactLinkSx,
                      py: 0.65,
                      px: 1.25,
                      fontSize: 12.5,
                      justifyContent: 'flex-start',
                    }}
                  >
                    <PhoneOutlinedIcon
                      sx={{ fontSize: 16, color: colors.primary }}
                    />
                    +39 377 461 2000
                  </Box>
                  <Box
                    component="a"
                    href="tel:+393496033063"
                    sx={{
                      ...contactLinkSx,
                      py: 0.65,
                      px: 1.25,
                      fontSize: 12.5,
                      justifyContent: 'flex-start',
                    }}
                  >
                    <PhoneOutlinedIcon
                      sx={{ fontSize: 16, color: colors.primary }}
                    />
                    +39 349 603 3063
                  </Box>
                  <Box
                    component="a"
                    href="mailto:zonagalatea@sicilia.agesci.it"
                    sx={{
                      ...contactLinkSx,
                      py: 0.65,
                      px: 1.25,
                      fontSize: 12,
                      justifyContent: 'flex-start',
                      wordBreak: 'break-all',
                    }}
                  >
                    <MailOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: colors.primary,
                        flexShrink: 0,
                      }}
                    />
                    zonagalatea@sicilia.agesci.it
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* Map: full width on mobile, half of footer on desktop */}
            <Box
              sx={{
                flex: { sm: '1 1 0' },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 0 },
                minHeight: { xs: 150, sm: 'auto' },
                alignSelf: 'stretch',
                borderRadius: '16px',
                overflow: 'hidden',
                border: `1px solid ${colors.outlineVariant}`,
                bgcolor: colors.surfaceContainerLow,
              }}
            >
              <Box
                component="iframe"
                title="Mappa Sede di Zona — Via Rocco Chinnici, Acireale"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d468.4903319269039!2d15.163735025420474!3d37.634863476295585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1313f8fa71d3745f%3A0xfdac561bcf4315aa!2sVia%20Rocco%20Chinnici%2C%2095024%20Acireale%20CT%2C%20Italia!5e1!3m2!1sit!2sid!4v1791199263982!5m2!1sit!2sid"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                sx={{
                  border: 0,
                  width: '100%',
                  height: '100%',
                  minHeight: { xs: 150, sm: 160 },
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {floating}
    </Box>
  );
}
